import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { FC } from 'react';

import ChevronLeft from '@/assets/icons/chevron-left.svg';
import ChevronRight from '@/assets/icons/chevron-right.svg';
import Warning from '@/assets/icons/warning.svg';
import { getContractTypeLabel } from '@/components/listing/contract/Contract.utils';
import { PAGE_VIEW_TITLE } from '@/utils/gtm';
import { generateButtonId } from '@/utils/tracking';
import { getFullName } from '@proprioo/hokkaido';
import {
  Button,
  ButtonAppearance,
  ButtonSizes,
  IconPosition
} from '@proprioo/salatim';

import {
  ButtonsWrapper,
  ExistingContractMessage,
  ExistingCustomer,
  ExistingMessage,
  WarningWrapper
} from './ExistingCustomerWarning.styles';
import { ExistingCustomerWithContractProps } from './interfaces';
import { getFilteredContracts } from './utils';

const ExistingCustomerWithContract: FC<ExistingCustomerWithContractProps> = ({
  customer,
  existingContracts,
  fetchedCustomer,
  onCancel
}) => {
  const { t } = useTranslation();

  const filteredContracts = getFilteredContracts(existingContracts);
  const originalCustomer = getFullName(customer.firstName, customer.lastName);
  const newCustomer = getFullName(
    fetchedCustomer.user.firstName,
    fetchedCustomer.user.lastName
  );

  return (
    <ExistingCustomer>
      <WarningWrapper>
        <Warning aria-label={t('warning')} />
      </WarningWrapper>
      <ExistingMessage
        data-test="existing-contracts-message-text"
        dangerouslySetInnerHTML={{
          __html: t('customersMergeContractError', {
            newFullName: newCustomer,
            oldFullName: originalCustomer
          })
        }}
      />
      {filteredContracts.map(
        ({ documentType, id: contractId, listingId }, id) => (
          <ExistingContractMessage
            key={id}
            data-test={`existing-contract-${contractId}`}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: t('customerContractOnListing', {
                  contractTypeLabel: t(getContractTypeLabel(documentType)),
                  listingId
                })
              }}
            />
            <Link
              href={`/f/${listingId}`}
              data-test={`existing-contract-link-${listingId}`}
              id={generateButtonId(
                PAGE_VIEW_TITLE.MODAL_CUSTOMER_EXISTING_EDIT,
                'contract'
              )}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Button
                appearance={ButtonAppearance.SECONDARY}
                icon={<ChevronRight />}
                label={t('contract')}
                rounded={true}
                size={ButtonSizes.SMALL}
              />
            </Link>
          </ExistingContractMessage>
        )
      )}
      <ButtonsWrapper>
        <Button
          appearance={ButtonAppearance.SECONDARY}
          icon={<ChevronLeft />}
          iconPosition={IconPosition.LEFT}
          label={t('back')}
          onClick={() => onCancel()}
          size={ButtonSizes.SMALL}
        />
      </ButtonsWrapper>
    </ExistingCustomer>
  );
};

export default ExistingCustomerWithContract;
